import {Form, FormField, FormSubmitHandler, showNotification} from 'platform/components';
import {Grid, VStack} from 'platform/foundation';
import {z} from 'zod';

import {useState} from 'react';

import {isNotNil} from 'ramda';

import {
  AccGetAccountingContextCodeTableIdApiResponse,
  AccGetMatrix202and205ApiResponse,
  usePostAccountingContextCodeTableMutation,
} from '@dms/api/finance';
import {GetAuthorizationProfileResponse} from '@dms/api/security';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {ApiError, useNavigate, useRequiredParams} from 'shared/web';

interface AccountingCodesDetailFormProps {
  accountingCodeDetail: AccGetAccountingContextCodeTableIdApiResponse;
  authorizationProfile: GetAuthorizationProfileResponse;
  codes: AccGetMatrix202and205ApiResponse;
}

export function AccountingCodesDetailForm(props: AccountingCodesDetailFormProps) {
  const {id} = useRequiredParams();
  const navigate = useNavigate();
  const [accountingOptions, setAccountingOptions] = useState<{label: string; value: string}[]>(
    props.codes.caseType.map((code) => ({
      label: code,
      value: code,
    }))
  );
  const [updateAccountingCodeDetail] = usePostAccountingContextCodeTableMutation();

  const handleSubmit: FormSubmitHandler<FormValues> = async (
    values,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    await updateAccountingCodeDetail({
      attributeName: 'sale-case-type',
      body: {
        referenceId: id,
        accountingCode: values.accountingCode,
      },
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('entity.settings.accountingCodes.notificationSuccess'));
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.accountingCodes);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const defaultValues = {
    accountingCode: props.accountingCodeDetail?.accountingCode ?? '',
    authorizationProfile: props.authorizationProfile?.name ?? '',
  };

  return (
    <>
      <Form<FormValues>
        onSubmit={handleSubmit}
        defaultValues={defaultValues}
        experimentalZodSchema={formSchema}
        shouldWatchForUnsavedChanges
      >
        {(control, formApi, navigateWithoutBlocker) => (
          <>
            <VStack spacing={4} maxWidth={200}>
              <Grid spacing={4} columns={2}>
                <FormField
                  name="authorizationProfile"
                  control={control}
                  type="text"
                  isRequired
                  isDisabled
                  label={i18n.t('entity.settings.labels.authorizationProfile')}
                />
                <FormField
                  control={control}
                  type="creatableChoice"
                  name="accountingCode"
                  label={i18n.t('entity.settings.labels.accountingCode')}
                  options={accountingOptions}
                  onCreateOption={(inputValue) => {
                    const newOption = {
                      label: inputValue,
                      value: inputValue,
                    };
                    setAccountingOptions((prev) => [...prev, newOption]);
                    formApi.setValue('accountingCode', inputValue);
                    return newOption;
                  }}
                  data-testid={testIds.settings.accountingCodesDetail('accountingCode')}
                />
              </Grid>
            </VStack>

            <SettingsFooter
              actions={[
                {
                  title: i18n.t('general.actions.discard'),
                  variant: 'secondary',
                  onClick: () => navigateWithoutBlocker(settingsRoutes.accountingCodes),
                  type: 'button',
                },
                {
                  type: 'form-button',
                  control,
                  buttonType: 'submit',
                  title: isNotNil(id)
                    ? i18n.t('general.actions.save')
                    : i18n.t('general.actions.create'),
                },
              ]}
              data-testid={testIds.settings.accountingCodesDetail('footer')}
            />
          </>
        )}
      </Form>
    </>
  );
}

type FormValues = z.infer<typeof formSchema>;
const formSchema = z.object({
  authorizationProfile: z.string(),
  accountingCode: z.string().nullable().optional(),
});
